// Interface for save response
export interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

// Interface for file display data
export interface FileDisplayData {
  id: string;
  cycleNum: number;
  productName?: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  uploadTime: number;
  isOffline: boolean;
}

// SharePoint site URL
const SP_SITE_URL = "https://bectors.sharepoint.com/sites/PTMS_PRD";
const SP_LIBRARY_TITLE = "BakingandCoolingDocuments";

/**
 * Get SharePoint internal field names for the list
 * SharePoint uses internal names that may differ from display names
 */
async function getSPFieldInternalNames(accessToken: string): Promise<{
  qualityInternal: string;
  cycleInternal: string;
  productInternal: string;
  entityType: string;
}> {
  try {
    // Get list fields to discover internal names
    const fieldsUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/fields?$filter=Hidden eq false&$select=InternalName,Title`;
    const fieldsRes = await fetch(fieldsUrl, {
      method: "GET",
      headers: {
        "Accept": "application/json;odata=verbose",
        "Authorization": `Bearer ${accessToken}`
      }
    });

    let qualityInternal = "QualityId";
    let cycleInternal = "CycleNum";
    let productInternal = "ProductName";

    if (fieldsRes.ok) {
      const fieldsData = await fieldsRes.json();
      const fields = fieldsData.d?.results || [];
      
      // Find internal names by matching display names
      for (const field of fields) {
        const title = (field.Title || "").toLowerCase();
        const internalName = field.InternalName || "";
        
        if (title.includes("quality") || title.includes("qualityid") || title.includes("quality id")) {
          qualityInternal = internalName;
        }
        if (title.includes("cycle") && (title.includes("num") || title.includes("number") || title.includes("no"))) {
          cycleInternal = internalName;
        }
        if (title.includes("product") && title.includes("name")) {
          productInternal = internalName;
        }
      }
      
      console.log('Discovered SharePoint field names:', { qualityInternal, cycleInternal, productInternal });
    }

    // Get entity type
    const entityRes = await fetch(
      `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')?$select=ListItemEntityTypeFullName`,
      {
        method: "GET",
        headers: { 
          "Accept": "application/json;odata=verbose",
          "Authorization": `Bearer ${accessToken}`
        }
      }
    );

    let entityType = "SP.Data.BakingandCoolingDocumentsListItem";
    if (entityRes.ok) {
      const entityData = await entityRes.json();
      entityType = entityData.d?.ListItemEntityTypeFullName || entityType;
    }

    return { qualityInternal, cycleInternal, productInternal, entityType };
  } catch (error) {
    console.warn('Failed to discover field names, using defaults:', error);
    // Return default internal names (common SharePoint patterns)
    return {
      qualityInternal: "QualityId", // Try common variations
      cycleInternal: "CycleNum",    // Try common variations  
      productInternal: "ProductName",
      entityType: "SP.Data.BakingandCoolingDocumentsListItem"
    };
  }
}

/**
 * Upload file to SharePoint for Baking and Cooling
 * @param cycleNum - Cycle number (1 or 2)
 * @param QualityTourId - Quality Tour ID from Redux
 * @param accessToken - SharePoint access token from MSAL
 * @param productName - Current product name (scopes attachment to this product)
 * @returns Promise<SaveResponse>
 */
export async function uploadFileToSharePoint(cycleNum: number, QualityTourId: string, accessToken: string, productName?: string): Promise<SaveResponse> {
  try {
    if (!accessToken) {
      throw new Error('Access token is invalid or missing');
    }

    // Validate cycle number
    if (!cycleNum || cycleNum < 1) {
      throw new Error('Invalid cycle number');
    }

    // Get file input element
    const fileInput = document.getElementById(`attachment-${cycleNum}`) as HTMLInputElement;
    const file = fileInput?.files?.[0];

    if (!file) {
      return { success: false, message: "Please select a file before uploading" };
    }

    // Log upload parameters
    console.log('Uploading to SharePoint:', { cycleNum, productName, fileName: file.name, fileSize: file.size });

    // Get SharePoint context info (digest)
    const digestResponse = await fetch(
      `${SP_SITE_URL}/_api/contextinfo`,
      {
        method: "POST",
        headers: { 
          "Accept": "application/json;odata=verbose",
          "Authorization": `Bearer ${accessToken}`
        }
      }
    );

    if (!digestResponse.ok) {
      throw new Error(`Failed to get context info: ${digestResponse.status}`);
    }

    const digestData = await digestResponse.json();
    const digest = digestData.d.GetContextWebInformation.FormDigestValue;

    // Add date and time to filename to ensure uniqueness
    const fileExtension = file.name.split('.').pop() || '';
    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
    const timestamp = `${dateStr}_${timeStr}`;
    const uniqueFileName = `${fileNameWithoutExt}_${timestamp}.${fileExtension}`;

    // Upload file to SharePoint - Use server-relative path (matching working example)
    const folderServerRelativeUrl = "/sites/PTMS_PRD/BakingandCoolingDocuments";
    const uploadUrl = `${SP_SITE_URL}/_api/web/GetFolderByServerRelativeUrl('${folderServerRelativeUrl}')/Files/add(url='${encodeURIComponent(uniqueFileName)}',overwrite=true)?$expand=ListItemAllFields`;

    console.log('Uploading file to SharePoint:', { 
      uploadUrl, 
      originalFileName: file.name, 
      uniqueFileName: uniqueFileName,
      fileSize: file.size 
    });

    try {
      const uploadRes = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "Accept": "application/json;odata=verbose",
          "X-RequestDigest": digest,
          "Authorization": `Bearer ${accessToken}`
        },
        body: file
      });

      if (!uploadRes.ok) {
        const errorText = await uploadRes.text();
        console.error('File upload failed:', {
          status: uploadRes.status,
          statusText: uploadRes.statusText,
          error: errorText,
          uploadUrl
        });
        throw new Error(`Upload failed: ${uploadRes.status} - ${errorText}`);
      }

      const uploadJson = await uploadRes.json();
      console.log('File upload successful, response:', uploadJson);
      
      const itemId = uploadJson?.d?.ListItemAllFields?.Id || uploadJson?.d?.Id || uploadJson?.d?.ListItemAllFields?.ID;

      if (!itemId) {
        console.error('Upload response missing itemId:', uploadJson);
        throw new Error("Upload succeeded but ListItem Id not found in response.");
      }
      
      console.log('File uploaded successfully to document library:', { 
        itemId, 
        originalFileName: file.name, 
        savedFileName: uniqueFileName 
      });

      // Get list item entity type
      const entityResponse = await fetch(
        `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')?$select=ListItemEntityTypeFullName`,
        {
          method: "GET",
          headers: { 
            "Accept": "application/json;odata=verbose",
            "Authorization": `Bearer ${accessToken}`
          }
        }
      );

      if (!entityResponse.ok) {
        console.warn(`Failed to get entity type: ${entityResponse.status}, but file is uploaded`);
        // File is uploaded, return success even if we can't update metadata
        return {
          success: true,
          message: `File "${uniqueFileName}" uploaded successfully to document library. Metadata update skipped.`,
          data: { itemId, uploadJson, fileName: uniqueFileName }
        };
      }

      // File is uploaded successfully at this point - now update metadata
      // Even if metadata update fails, file should still be saved
      try {
      // Get SharePoint internal field names
      const SP_FIELDS = await getSPFieldInternalNames(accessToken);
      console.log('Using SharePoint field names:', SP_FIELDS);

      // Clean QualityTourId (remove braces if present)
      const qid = (QualityTourId || "").replace(/[{}]/g, "");

      // Update metadata: QualityId required; CycleNum and ProductName scope files per cycle/product
      const itemUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items(${itemId})`;

      // Ensure cycleNum is a valid number and convert to string (as shown in working example)
      const cycleNumValue = Number(cycleNum);
      if (isNaN(cycleNumValue) || cycleNumValue < 1) {
        console.warn(`Invalid cycle number: ${cycleNum}, skipping metadata update`);
        // File is uploaded, return success even if metadata fails
        return {
          success: true,
          message: `File "${uniqueFileName}" uploaded successfully, but metadata update skipped due to invalid cycle number.`,
          data: { itemId, uploadJson, fileName: uniqueFileName }
        };
      }
      
      // Use INTERNAL field names (like the working example)
      const updateBodyFull: Record<string, unknown> = {
        __metadata: { type: SP_FIELDS.entityType },
        [SP_FIELDS.qualityInternal]: qid,
        [SP_FIELDS.cycleInternal]: String(cycleNumValue), // Convert to string as in working example
      };
      
      // Add ProductName if it's provided and not empty
      if (productName != null && productName !== "") {
        updateBodyFull[SP_FIELDS.productInternal] = productName.trim();
      }
      
      console.log('Updating SharePoint metadata with INTERNAL names:', { 
        cycleNum: cycleNumValue, 
        productName, 
        QualityId: qid, 
        updateBody: updateBodyFull,
        fieldNames: SP_FIELDS
      });

      const doUpdate = (body: object) =>
        fetch(itemUrl, {
          method: "POST",
          headers: {
            "Accept": "application/json;odata=verbose",
            "Content-Type": "application/json;odata=verbose",
            "X-RequestDigest": digest,
            "Authorization": `Bearer ${accessToken}`,
            "IF-MATCH": "*",
            "X-HTTP-Method": "MERGE"
          },
          body: JSON.stringify(body)
        });

      let updateRes = await doUpdate(updateBodyFull as object);
      let cycleNumSaved = false;
      
      // Check if update was successful
      if (updateRes.ok) {
        console.log('All metadata updated successfully in one call:', { cycleNum: cycleNumValue, productName });
        cycleNumSaved = true;
      } else {
        // If update fails, try updating fields separately
        const errorText = await updateRes.text();
        console.warn('Full metadata update failed:', updateRes.status, errorText);
        
        // First, ensure QualityId is set using INTERNAL name
        const updateBodyQualityOnly = { 
          __metadata: { type: SP_FIELDS.entityType }, 
          [SP_FIELDS.qualityInternal]: qid 
        };
        const qualityOnlyRes = await doUpdate(updateBodyQualityOnly);
        
        if (!qualityOnlyRes.ok) {
          const qualityError = await qualityOnlyRes.text();
          console.warn('QualityId update failed, but file is uploaded:', qualityError);
          // Don't throw - file is already uploaded
        } else {
          console.log('QualityId updated successfully');
        }
        
        // CRITICAL: Try to update CycleNum separately using INTERNAL name
        if (!isNaN(cycleNumValue) && cycleNumValue >= 1) {
          try {
            const cycleUpdateBody = { 
              __metadata: { type: SP_FIELDS.entityType }, 
              [SP_FIELDS.cycleInternal]: String(cycleNumValue) // Convert to string as in working example
            };
            console.log('Attempting to update CycleNum separately using internal name:', SP_FIELDS.cycleInternal, cycleNumValue);
            const cycleRes = await doUpdate(cycleUpdateBody);
            if (!cycleRes.ok) {
              const cycleError = await cycleRes.text();
              console.error('CycleNum update failed:', cycleRes.status, cycleError);
            } else {
              console.log('✓ CycleNum updated successfully:', cycleNumValue);
              cycleNumSaved = true;
            }
          } catch (e) {
            console.error('CycleNum update exception:', e);
          }
        } else {
          console.error('Invalid cycle number:', cycleNum);
        }
        
        // Try updating ProductName separately using INTERNAL name
        if (productName != null && productName !== "") {
          try {
            const productUpdateBody = { 
              __metadata: { type: SP_FIELDS.entityType }, 
              [SP_FIELDS.productInternal]: productName.trim() 
            };
            const productRes = await doUpdate(productUpdateBody);
            if (!productRes.ok) {
              const productError = await productRes.text();
              console.error('ProductName update failed:', productRes.status, productError);
            } else {
              console.log('✓ ProductName updated successfully:', productName);
            }
          } catch (e) {
            console.error('ProductName update error:', e);
          }
        }
      }
      
      // Final check - warn if cycle number wasn't saved, but don't fail the upload
      if (!cycleNumSaved) {
        console.error('❌ WARNING: CycleNum was NOT saved to SharePoint!', { cycleNum: cycleNumValue, itemId });
        // File is uploaded, but metadata might be missing - still return success
        return {
          success: true,
          message: `File "${uniqueFileName}" uploaded successfully, but cycle number metadata may not have been saved. Check console.`,
          data: { itemId, uploadJson, fileName: uniqueFileName }
        };
      } else {
        console.log('✓ CycleNum confirmed saved:', cycleNumValue);
      }

      return {
        success: true,
        message: `File "${uniqueFileName}" uploaded and tagged successfully (QualityId + CycleNum).`,
        data: { itemId, uploadJson, fileName: uniqueFileName }
      };
    } catch (metadataError) {
      // Metadata update failed, but file is already uploaded - return success
      console.error('Metadata update failed, but file was uploaded:', metadataError);
      return {
        success: true,
        message: `File "${uniqueFileName}" uploaded successfully to document library. Metadata update failed but file is saved.`,
        data: { itemId, uploadJson, fileName: uniqueFileName }
      };
    }
    } catch (uploadError) {
      // File upload itself failed
      console.error('File upload error:', uploadError);
      throw uploadError; // Re-throw to be caught by outer catch
    }

  } catch (err) {
    console.error("Upload error:", err);
    const errorMessage = err instanceof Error ? err.message : "Failed to upload file. Check console.";
    console.error("Full error details:", err);
    return {
      success: false,
      message: errorMessage
    };
  }
}

/**
 * Upload file to SharePoint with File parameter (for offline sync)
 * @param cycleNum - Cycle number
 * @param QualityTourId - Quality Tour ID from Redux
 * @param accessToken - SharePoint access token from MSAL
 * @param file - File object to upload
 * @param productName - Product name (optional)
 * @returns Promise<SaveResponse>
 */
export async function uploadFileToSharePointWithFile(
  cycleNum: number,
  QualityTourId: string,
  accessToken: string,
  file: File,
  productName?: string
): Promise<SaveResponse> {
  try {
    if (!accessToken) {
      throw new Error('Access token is invalid or missing');
    }

    if (!file) {
      return { success: false, message: "File is required" };
    }

    // Validate cycle number
    if (!cycleNum || cycleNum < 1) {
      throw new Error('Invalid cycle number');
    }

    // Log upload parameters
    console.log('Uploading to SharePoint (with file param):', { cycleNum, productName, fileName: file.name, fileSize: file.size });

    // Get SharePoint context info (digest)
    const digestResponse = await fetch(
      `${SP_SITE_URL}/_api/contextinfo`,
      {
        method: "POST",
        headers: { 
          "Accept": "application/json;odata=verbose",
          "Authorization": `Bearer ${accessToken}`
        }
      }
    );

    if (!digestResponse.ok) {
      throw new Error(`Failed to get context info: ${digestResponse.status}`);
    }

    const digestData = await digestResponse.json();
    const digest = digestData.d.GetContextWebInformation.FormDigestValue;

    // Add date and time to filename to ensure uniqueness
    const fileExtension = file.name.split('.').pop() || '';
    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
    const timestamp = `${dateStr}_${timeStr}`;
    const uniqueFileName = `${fileNameWithoutExt}_${timestamp}.${fileExtension}`;

    // Upload file to SharePoint
    const folderServerRelativeUrl = "/sites/PTMS_PRD/BakingandCoolingDocuments";
    const uploadUrl = `${SP_SITE_URL}/_api/web/GetFolderByServerRelativeUrl('${folderServerRelativeUrl}')/Files/add(url='${encodeURIComponent(uniqueFileName)}',overwrite=true)?$expand=ListItemAllFields`;

    console.log('Uploading file to SharePoint:', { 
      uploadUrl, 
      originalFileName: file.name, 
      uniqueFileName: uniqueFileName,
      fileSize: file.size 
    });

    const uploadRes = await fetch(uploadUrl, {
      method: "POST",
      headers: {
        "Accept": "application/json;odata=verbose",
        "X-RequestDigest": digest,
        "Authorization": `Bearer ${accessToken}`
      },
      body: file
    });

    if (!uploadRes.ok) {
      const errorText = await uploadRes.text();
      console.error('File upload failed:', {
        status: uploadRes.status,
        statusText: uploadRes.statusText,
        error: errorText,
        uploadUrl
      });
      throw new Error(`Upload failed: ${uploadRes.status} - ${errorText}`);
    }

    const uploadJson = await uploadRes.json();
    console.log('File upload successful, response:', uploadJson);
    
    const itemId = uploadJson?.d?.ListItemAllFields?.Id || uploadJson?.d?.Id || uploadJson?.d?.ListItemAllFields?.ID;

    if (!itemId) {
      console.error('Upload response missing itemId:', uploadJson);
      throw new Error("Upload succeeded but ListItem Id not found in response.");
    }
    
    console.log('File uploaded successfully to document library:', { 
      itemId, 
      originalFileName: file.name, 
      savedFileName: uniqueFileName 
    });

    // Get SharePoint internal field names and update metadata
    try {
      const SP_FIELDS = await getSPFieldInternalNames(accessToken);
      console.log('Using SharePoint field names:', SP_FIELDS);

      const qid = (QualityTourId || "").replace(/[{}]/g, "");
      const itemUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items(${itemId})`;

      const cycleNumValue = Number(cycleNum);
      if (isNaN(cycleNumValue) || cycleNumValue < 1) {
        console.warn(`Invalid cycle number: ${cycleNum}, skipping metadata update`);
        return {
          success: true,
          message: `File "${uniqueFileName}" uploaded successfully, but metadata update skipped due to invalid cycle number.`,
          data: { itemId, uploadJson, fileName: uniqueFileName }
        };
      }
      
      const updateBodyFull: Record<string, unknown> = {
        __metadata: { type: SP_FIELDS.entityType },
        [SP_FIELDS.qualityInternal]: qid,
        [SP_FIELDS.cycleInternal]: String(cycleNumValue),
      };
      
      if (productName != null && productName !== "") {
        updateBodyFull[SP_FIELDS.productInternal] = productName.trim();
      }
      
      console.log('Updating SharePoint metadata:', { 
        cycleNum: cycleNumValue, 
        productName, 
        QualityId: qid, 
        updateBody: updateBodyFull
      });

      const doUpdate = (body: object) =>
        fetch(itemUrl, {
          method: "POST",
          headers: {
            "Accept": "application/json;odata=verbose",
            "Content-Type": "application/json;odata=verbose",
            "X-RequestDigest": digest,
            "Authorization": `Bearer ${accessToken}`,
            "IF-MATCH": "*",
            "X-HTTP-Method": "MERGE"
          },
          body: JSON.stringify(body)
        });

      let updateRes = await doUpdate(updateBodyFull as object);
      
      if (!updateRes.ok) {
        const errorText = await updateRes.text();
        console.warn('Full metadata update failed:', updateRes.status, errorText);
        
        // Try updating QualityId first
        const updateBodyQualityOnly = { 
          __metadata: { type: SP_FIELDS.entityType }, 
          [SP_FIELDS.qualityInternal]: qid 
        };
        await doUpdate(updateBodyQualityOnly);
        
        // Then try CycleNum
        const updateBodyCycle = { 
          __metadata: { type: SP_FIELDS.entityType }, 
          [SP_FIELDS.cycleInternal]: String(cycleNumValue)
        };
        await doUpdate(updateBodyCycle);
        
        // Finally try ProductName if provided
        if (productName != null && productName !== "") {
          const updateBodyProduct = { 
            __metadata: { type: SP_FIELDS.entityType }, 
            [SP_FIELDS.productInternal]: productName.trim()
          };
          await doUpdate(updateBodyProduct);
        }
      }

      return {
        success: true,
        message: `File "${uniqueFileName}" uploaded successfully.`,
        data: { itemId, uploadJson, fileName: uniqueFileName }
      };
    } catch (metadataError) {
      console.error('Metadata update error:', metadataError);
      // File is uploaded, return success even if metadata fails
      return {
        success: true,
        message: `File "${uniqueFileName}" uploaded successfully. Metadata update failed but file is saved.`,
        data: { itemId, uploadJson, fileName: uniqueFileName }
      };
    }
  } catch (error) {
    console.error("Upload error:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to upload file"
    };
  }
}

/**
 * Fetch uploaded files from SharePoint for Baking and Cooling
 * Each file's cycleNum is read from the document library CycleNum column so attachments display under their respective cycle.
 * @param QualityTourId - Quality Tour ID from Redux
 * @param accessToken - SharePoint access token from MSAL
 * @returns Promise<FileDisplayData[]> - each file has cycleNum from the library
 */
export async function fetchUploadedFiles(QualityTourId: string, accessToken: string): Promise<FileDisplayData[]> {
  try {
    if (!accessToken) {
      throw new Error('Access token is invalid or missing');
    }

    // Clean QualityTourId (remove braces if present)
    const qid = (QualityTourId || "").replace(/[{}]/g, "");

    // Get list field internal names so we request and read CycleNum/ProductName correctly (avoids wrong-cycle display when same product name is used for multiple cycles)
    let cycleInternal = "CycleNum";
    let productInternal = "ProductName";
    try {
      const fieldNames = await getSPFieldInternalNames(accessToken);
      cycleInternal = fieldNames.cycleInternal;
      productInternal = fieldNames.productInternal;
    } catch (e) {
      console.warn('Using default field names for CycleNum/ProductName', e);
    }

    // Fetch: try multiple ways to get CycleNum so cycle 2 images don't show in cycle 1
    const fetchUrlWithMeta = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items?$filter=QualityId eq '${qid}'&$select=Id,File/Name,File/ServerRelativeUrl,File/Length,Modified,${cycleInternal},${productInternal}&$expand=File`;
    const fetchUrlLiteralMeta = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items?$filter=QualityId eq '${qid}'&$select=Id,File/Name,File/ServerRelativeUrl,File/Length,Modified,CycleNum,ProductName&$expand=File`;
    const fetchUrlSelectAll = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items?$filter=QualityId eq '${qid}'&$select=*&$expand=File`;
    const fetchUrlBasic = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items?$filter=QualityId eq '${qid}'&$select=Id,File/Name,File/ServerRelativeUrl,File/Length,Modified&$expand=File`;

    const headers = { "Accept": "application/json;odata=verbose", "Authorization": `Bearer ${accessToken}` };
    let response = await fetch(fetchUrlWithMeta, { method: "GET", headers });

    let usedBasicSelect = false;
    if (!response.ok || response.status === 400) {
      response = await fetch(fetchUrlLiteralMeta, { method: "GET", headers });
    }
    if (!response.ok || response.status === 400) {
      response = await fetch(fetchUrlSelectAll, { method: "GET", headers });
    }
    if (!response.ok || response.status === 400) {
      usedBasicSelect = true;
      response = await fetch(fetchUrlBasic, { method: "GET", headers });
    }

    if (!response.ok) {
      console.error(`Failed to fetch files: ${response.status}`);
      return [];
    }

    const data = await response.json();
    const items = data.d?.results || [];
    const hasMeta = !usedBasicSelect;

    // Helper: read cycle number from item (document library CycleNum column). Always try every possible key so cycle 2 images don't show in cycle 1.
    const getCycleValueFromItem = (item: any): unknown => {
      const tryVal = (v: unknown): unknown => {
        if (v == null || v === '') return null;
        if (typeof v === 'object' && 'Value' in v) return (v as { Value: unknown }).Value;
        if (typeof v === 'object' && 'value' in v) return (v as { value: unknown }).value;
        return v;
      };
      let v: unknown;
      v = tryVal(item[cycleInternal]); if (v != null && v !== '') return v;
      v = tryVal(item.CycleNum); if (v != null && v !== '') return v;
      v = tryVal(item.cycleNum); if (v != null && v !== '') return v;
      const cycleKeyExact = Object.keys(item || {}).find((k) => k.toLowerCase() === 'cyclenum' || k.toLowerCase() === 'cycle0x0020num');
      if (cycleKeyExact) {
        v = tryVal(item[cycleKeyExact]);
        if (v != null && v !== '') return v;
      }
      // Always scan for any key containing "cycle" (works with $select=* and any internal name)
      for (const k of Object.keys(item || {})) {
        if (k.toLowerCase().includes('cycle') && !k.toLowerCase().includes('recycle')) {
          v = tryVal(item[k]);
          if (v != null && v !== '') return v;
        }
      }
      return null;
    };

    const getProductValueFromItem = (item: any): string | null => {
      if (!hasMeta) return null;
      const v = item[productInternal];
      if (v != null && String(v).trim() !== '') return String(v).trim();
      if (item.ProductName != null && String(item.ProductName).trim() !== '') return String(item.ProductName).trim();
      const productKey = Object.keys(item || {}).find((k) => k.toLowerCase() === 'productname');
      if (productKey) {
        const val = item[productKey];
        if (val != null && String(val).trim() !== '') return String(val).trim();
      }
      return null;
    };

    // Build file URL: ServerRelativeUrl is server-relative (e.g. /sites/PTMS_PRD/...), so use origin only
    const spOrigin = new URL(SP_SITE_URL).origin;
    // Map SharePoint items to FileDisplayData using internal field names; filter by cycleNum in UI, not product name, so cycle 2 files don't show under cycle 1 when product name is same
    const files: FileDisplayData[] = items.map((item: any) => {
      const serverRelative = item.File?.ServerRelativeUrl;
      const rawUrl = serverRelative ? `${spOrigin}${serverRelative}` : '';
      const fileUrl = rawUrl ? `${rawUrl}?web=1` : '';

      const rawCycle = getCycleValueFromItem(item);
      const rawProduct = getProductValueFromItem(item);

      // Parse CycleNum - handle both string and number formats; use cycle number from library, not product name
      let cycleNum = 1;
      if (rawCycle != null && rawCycle !== '') {
        const cycleValue = rawCycle;
        if (typeof cycleValue === 'string') {
          const numMatch = cycleValue.match(/\d+/);
          cycleNum = numMatch ? Number(numMatch[0]) : 1;
        } else {
          cycleNum = Number(cycleValue);
        }
        if (isNaN(cycleNum) || cycleNum < 1) {
          cycleNum = 1;
        }
      }

      const productName = rawProduct != null && rawProduct !== '' ? rawProduct : undefined;

      if (!hasMeta || rawCycle == null) {
        console.warn('File missing CycleNum metadata:', {
          fileName: item.File?.Name,
          hasMeta,
          [cycleInternal]: rawCycle,
          parsedCycleNum: cycleNum,
          allKeys: Object.keys(item)
        });
      }

      return {
        id: item.Id.toString(),
        cycleNum,
        productName,
        fileUrl,
        fileName: item.File?.Name || 'Unknown',
        fileSize: item.File?.Length || 0,
        uploadTime: new Date(item.Modified).getTime(),
        isOffline: false
      };
    });
    
    console.log(`Fetched ${files.length} files from SharePoint:`, files.map(f => ({ 
      fileName: f.fileName, 
      cycleNum: f.cycleNum, 
      productName: f.productName || '(no product name)',
      fileUrl: f.fileUrl ? 'present' : 'missing'
    })));

    return files;

  } catch (err) {
    console.error("Error fetching uploaded files:", err);
    return [];
  }
}

/**
 * Delete file from SharePoint for Baking and Cooling
 * @param fileId - SharePoint list item ID
 * @param accessToken - SharePoint access token from MSAL
 * @returns Promise<SaveResponse>
 */
export async function deleteFileFromSharePoint(fileId: string, accessToken: string): Promise<SaveResponse> {
  try {
    if (!accessToken) {
      throw new Error('Access token is invalid or missing');
    }

    if (!fileId) {
      throw new Error('File ID is required');
    }

    // Get SharePoint context info (digest)
    const digestResponse = await fetch(
      `${SP_SITE_URL}/_api/contextinfo`,
      {
        method: "POST",
        headers: { 
          "Accept": "application/json;odata=verbose",
          "Authorization": `Bearer ${accessToken}`
        }
      }
    );

    if (!digestResponse.ok) {
      throw new Error(`Failed to get context info: ${digestResponse.status}`);
    }

    const digestData = await digestResponse.json();
    const digest = digestData.d.GetContextWebInformation.FormDigestValue;

    // Delete the list item (which also deletes the file)
    const deleteUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items(${fileId})`;

    const deleteRes = await fetch(deleteUrl, {
      method: "POST",
      headers: {
        "Accept": "application/json;odata=verbose",
        "X-RequestDigest": digest,
        "Authorization": `Bearer ${accessToken}`,
        "IF-MATCH": "*",
        "X-HTTP-Method": "DELETE"
      }
    });

    if (!deleteRes.ok) {
      const errorText = await deleteRes.text();
      throw new Error(`Delete failed: ${deleteRes.status} - ${errorText}`);
    }

    return {
      success: true,
      message: "File deleted successfully"
    };

  } catch (err) {
    console.error("Delete error:", err);
    return {
      success: false,
      message: err instanceof Error ? err.message : "Failed to delete file. Check console."
    };
  }
}
